// scenarioSaga — orchestrates scenario loading, switching, and import.
//
// Pattern: each saga handler delegates async work to a typed async function,
// then dispatches the typed result. No `any` generators — all business logic
// lives in plain async functions with full type safety.

import { takeLatest, put, call, select } from 'redux-saga/effects';
import { createAction, type PayloadAction } from '@reduxjs/toolkit';
import { loadTopology } from '../slices/topologySlice.ts';
import { loadScenario } from '../actions/loadScenario.ts';
import { resetSimulation, clearSuiteResults } from '../slices/simulationSlice.ts';
import { setAppView } from '../slices/uiSlice.ts';
import { resetEvents } from '../slices/eventsSlice.ts';
import {
  manifestLoaded,
  scenarioLoaded,
  topologyLoaded,
  setActiveScenario,
  setLoading,
  setError,
  setWarnings,
  clearSource,
  setActiveSource,
  selectCachedScenario as selectSliceCachedScenario,
  selectCachedTopology as selectSliceCachedTopology,
  type ScenarioSourceId,
} from '../slices/scenarioSourceSlice.ts';
import type { RootState } from '../index.ts';
import {
  fetchManifest,
  fetchTopology,
  fetchScenario,
  findHappyPath,
  getCachedScenario,
  getCachedTopology,
  BASE_URL,
} from '../../services/RemoteScenarioLoader.ts';
import { fetchArtifacts } from '../../services/ArtifactLoaderService.ts';
import { importFileSet, type ImportWarning } from '../../services/ScenarioImportService.ts';
import type {
  FlowManifest,
  FlowEntry,
  ManifestEntry,
  SimulationScenario,
  SimulationTopology,
} from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Intent actions — dispatched by components
// ---------------------------------------------------------------------------

export const loadDefaultExample = createAction('scenario/loadDefaultExample');

export const switchScenario = createAction<{
  source: ScenarioSourceId;
  flow: FlowEntry;
  entry: ManifestEntry;
}>('scenario/switch');

export const importUserFiles = createAction<{
  files: File[];
  extraWarnings?: ImportWarning[];
}>('scenario/importUserFiles');

// ---------------------------------------------------------------------------
// Typed async orchestrators — all business logic with full type safety
// ---------------------------------------------------------------------------

interface DefaultExampleResult {
  manifest: FlowManifest;
  flow: FlowEntry;
  entry: ManifestEntry;
  topology: SimulationTopology;
  scenario: SimulationScenario;
}

async function orchestrateLoadDefault(): Promise<DefaultExampleResult> {
  const manifest = await fetchManifest();

  const result = findHappyPath(manifest);
  if (!result) {
    throw new Error('No happy path scenario found in manifest');
  }
  const [flow, entry] = result;

  // Fetch topology, scenario, and supplementary artifacts in parallel
  const [topology, scenario] = await Promise.all([fetchTopology(flow), fetchScenario(entry)]);
  await fetchArtifacts(BASE_URL, manifest.artifacts);

  return { manifest, flow, entry, topology, scenario };
}

interface SwitchScenarioResult {
  scenario: SimulationScenario;
  topology: SimulationTopology | null;
}

async function orchestrateSwitchExample(
  flow: FlowEntry,
  entry: ManifestEntry,
): Promise<SwitchScenarioResult> {
  const scenario = getCachedScenario(entry.scenarioId) ?? (await fetchScenario(entry));
  const topology = getCachedTopology(flow.flowId) ?? (await fetchTopology(flow));
  return { scenario, topology };
}

function buildUserSwitchResult(
  entry: ManifestEntry,
  flow: FlowEntry,
  scenario: SimulationScenario | null,
  topology: SimulationTopology | null,
): SwitchScenarioResult {
  if (!scenario) {
    throw new Error(`Scenario "${entry.scenarioLabel}" not uploaded. Upload the file to view it.`);
  }
  if (!topology) {
    throw new Error(
      `Topology for flow "${flow.flowName}" not uploaded. Upload the topology file to view scenarios in this flow.`,
    );
  }
  return { scenario, topology };
}

interface UserImportResult {
  manifest: FlowManifest;
  topologies: Record<string, SimulationTopology>;
  scenarios: Record<string, SimulationScenario>;
  warnings: ImportWarning[];
  happyPath: { flow: FlowEntry; entry: ManifestEntry } | null;
}

async function orchestrateImport(
  files: File[],
  extraWarnings?: ImportWarning[],
): Promise<UserImportResult> {
  const result = await importFileSet(files, extraWarnings);

  let happyPath: UserImportResult['happyPath'] = null;
  for (const flow of result.manifest.flows) {
    const happy = flow.scenarios.find((s) => s.isHappyPath);
    if (happy && result.scenarios[happy.scenarioId] && result.topologies[flow.flowId]) {
      happyPath = { flow, entry: happy };
      break;
    }
  }

  return { ...result, happyPath };
}

// ---------------------------------------------------------------------------
// Saga handlers — thin dispatch wrappers over typed orchestrators.
//
// The `yield call(asyncFn)` boundary is the only untyped seam. Each handler
// annotates the result with the orchestrator's return type so all downstream
// dispatches are fully typed.
// ---------------------------------------------------------------------------

function* handleLoadDefaultExample() {
  yield put(setLoading({ source: 'example', loading: true }));

  try {
    const result: DefaultExampleResult = yield call(orchestrateLoadDefault);

    yield put(manifestLoaded({ source: 'example', manifest: result.manifest }));
    yield put(loadTopology(result.topology));
    yield put(
      topologyLoaded({ source: 'example', flowId: result.flow.flowId, data: result.topology }),
    );
    yield put(
      scenarioLoaded({
        source: 'example',
        scenarioId: result.entry.scenarioId,
        data: result.scenario,
      }),
    );
    yield put(
      setActiveScenario({
        source: 'example',
        scenarioId: result.entry.scenarioId,
        flowId: result.flow.flowId,
      }),
    );
    yield put(loadScenario({ seed: result.scenario }));
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to load simulation data';
    yield put(setError({ source: 'example', error: msg }));
  } finally {
    yield put(setLoading({ source: 'example', loading: false }));
  }
}

function* handleSwitchScenario(
  action: PayloadAction<{ source: ScenarioSourceId; flow: FlowEntry; entry: ManifestEntry }>,
) {
  const { source, flow, entry } = action.payload;

  yield put(setLoading({ source, loading: true }));
  yield put(setError({ source, error: null }));

  try {
    let result: SwitchScenarioResult;

    if (source === 'example') {
      result = yield call(orchestrateSwitchExample, flow, entry);
    } else {
      // User/websocket source: read from Redux slice, not RemoteScenarioLoader cache
      const scenario: SimulationScenario | null = yield select((state: RootState) =>
        selectSliceCachedScenario(state, entry.scenarioId),
      );
      const topology: SimulationTopology | null = yield select((state: RootState) =>
        selectSliceCachedTopology(state, flow.flowId),
      );
      result = buildUserSwitchResult(entry, flow, scenario, topology);
    }

    if (result.topology) {
      yield put(topologyLoaded({ source, flowId: flow.flowId, data: result.topology }));
      yield put(loadTopology(result.topology));
    }
    if (source === 'example') {
      yield put(scenarioLoaded({ source, scenarioId: entry.scenarioId, data: result.scenario }));
    }
    yield put(setActiveScenario({ source, scenarioId: entry.scenarioId, flowId: flow.flowId }));
    // NOTE: no resetSimulation/resetEvents here — loadScenario extraReducers
    // already reset both slices while PRESERVING cumulative coverage and
    // suite results; a full reset would wipe them on every scenario switch.
    yield put(loadScenario({ seed: result.scenario }));
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to load scenario';
    yield put(setError({ source, error: msg }));
  } finally {
    yield put(setLoading({ source, loading: false }));
  }
}

function* handleImportUserFiles(
  action: PayloadAction<{ files: File[]; extraWarnings?: ImportWarning[] }>,
) {
  const { files, extraWarnings } = action.payload;

  // Clear stale data from any previous import
  yield put(clearSource('user'));
  yield put(setLoading({ source: 'user', loading: true }));

  try {
    const result: UserImportResult = yield call(orchestrateImport, files, extraWarnings);

    yield put(manifestLoaded({ source: 'user', manifest: result.manifest }));

    // A fresh import replaces the user source's data wholesale: make it the
    // ACTIVE source before anything runs (the walker's saga/policy tiers read
    // definitions source-aware), and drop suite rows keyed by the previous
    // manifest's scenarioIds — they are stale truth for this ledger.
    yield put(setActiveSource('user'));
    yield put(clearSuiteResults());

    for (const [flowId, topology] of Object.entries(result.topologies)) {
      yield put(topologyLoaded({ source: 'user', flowId, data: topology }));
    }
    for (const [scenarioId, scenario] of Object.entries(result.scenarios)) {
      yield put(scenarioLoaded({ source: 'user', scenarioId, data: scenario }));
    }

    // Surface warnings (missing files, ignored non-JSON files)
    if (result.warnings.length > 0) {
      yield put(
        setWarnings({
          source: 'user',
          warnings: result.warnings.map((w) => (w.file ? `${w.file}: ${w.message}` : w.message)),
        }),
      );
    }

    if (result.happyPath) {
      const { flow: hpFlow, entry: hpEntry } = result.happyPath;
      yield put(loadTopology(result.topologies[hpFlow.flowId]));
      yield put(
        setActiveScenario({
          source: 'user',
          scenarioId: hpEntry.scenarioId,
          flowId: hpFlow.flowId,
        }),
      );
      yield put(resetSimulation());
      yield put(resetEvents());
      yield put(loadScenario({ seed: result.scenarios[hpEntry.scenarioId] }));
    }

    // v2 IA: a successful upload lands the user in the portfolio ledger
    // (design: the upload card's action ends on the ledger, whose entry
    // auto-replays the suite). Previously the app stayed on the landing
    // screen, so post-upload clicks appeared to do nothing.
    yield put(setAppView('portfolio'));
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to import files';
    yield put(setError({ source: 'user', error: msg }));
  } finally {
    yield put(setLoading({ source: 'user', loading: false }));
  }
}

// ---------------------------------------------------------------------------
// Root scenario saga
// ---------------------------------------------------------------------------

export default function* scenarioSaga() {
  yield takeLatest(loadDefaultExample.type, handleLoadDefaultExample);
  yield takeLatest(switchScenario.type, handleSwitchScenario);
  yield takeLatest(importUserFiles.type, handleImportUserFiles);
}
